'use client'

import { cn } from '@/core/lib/utils/cn'
import { Motion } from '@/core/ui/atoms/motion'
import { useInView } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { useEffect, useRef, useState } from 'react'

type Stat = {
  label: string
  value: number
  suffix?: string
}

type Props = {
  className?: string
}

export const StatsBusiness: React.FC<Props> = ({ className }) => {
  const t = useTranslations('stats')

  const stats: Stat[] = [
    {
      label: t('trips'),
      value: 1000,
    },
    {
      label: t('customers'),
      value: 500,
    },
    {
      label: t('cities'),
      value: 20,
    },
  ]

  return (
    <section
      id='stats-section'
      className={cn('border-navy/10 w-full border-y bg-white', className)}
    >
      <div className='mx-auto grid max-w-6xl grid-cols-3'>
        {stats.map((stat, index) => (
          <StatItem
            key={stat.label}
            {...stat}
            index={index}
            showDivider={index !== stats.length - 1}
          />
        ))}
      </div>
    </section>
  )
}

type StatItemProps = Stat & {
  index: number
  showDivider: boolean
}

const StatItem: React.FC<StatItemProps> = ({
  label,
  value,
  index,
  showDivider,
}) => {
  const ref = useRef<HTMLDivElement>(null)

  const isInView = useInView(ref, {
    once: true,
    margin: '-80px',
  })

  const [count, setCount] = useState(0)

  useEffect(() => {
    if (!isInView) return

    const duration = 1400
    const startTime = performance.now()

    const animate = (time: number) => {
      const progress = Math.min((time - startTime) / duration, 1)

      const eased = 1 - Math.pow(1 - progress, 3)

      setCount(Math.floor(eased * value))

      if (progress < 1) {
        requestAnimationFrame(animate)
      }
    }

    const frame = requestAnimationFrame(animate)

    return () => cancelAnimationFrame(frame)
  }, [isInView, value])

  return (
    <Motion
      ref={ref}
      type='div'
      initial={{ opacity: 0, y: 15 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.6,
        delay: index * 0.1,
        ease: 'easeOut',
      }}
      className={cn(
        'relative flex flex-col items-center justify-center px-2 py-8 text-center sm:px-6 sm:py-10 md:py-12',
        showDivider &&
          'after:bg-navy/10 after:absolute after:top-1/2 after:right-0 after:h-10 after:w-px after:-translate-y-1/2 md:after:h-14',
      )}
    >
      <div className='flex items-baseline'>
        <span className='text-3xl font-semibold tracking-[-0.04em] text-[#06375a] sm:text-5xl md:text-6xl'>
          +{count}
        </span>
      </div>

      <span className='mt-2 text-[10px] font-semibold tracking-[0.12em] text-[#06375a]/55 uppercase sm:text-xs sm:tracking-[0.18em]'>
        {label}
      </span>
    </Motion>
  )
}
